import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';

const ARROW_RIGHT_SVG = [
    '<svg class="icon-arrow-right" width="100%" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">',
    '<path d="M6.94785 11.6698C6.95701 11.681 7.23791 11.7663 7.572 11.8594C7.90614 11.9525 8.18314 12.0144 8.18759 11.9971C8.192 11.9798 8.22658 11.6987 8.26438 11.3724C8.34032 10.7172 8.52419 9.95226 8.72066 9.47384C9.33306 7.98284 10.4318 6.99559 11.7645 6.73899L12.1038 6.67363L12.1038 6.01498L12.1038 5.35628L11.7963 5.29372C9.78111 4.88393 8.46734 3.05759 8.22157 0.324447C8.20553 0.145975 8.18636 1.24415e-07 8.17898 1.25163e-07C8.11101 1.32048e-07 6.98489 0.328962 6.96359 0.355022C6.94819 0.373865 6.95964 0.541177 6.98909 0.72676C7.31495 2.78178 8.248 4.454 9.45881 5.1532L9.74925 5.32093L3.77667 5.33406L-4.66991e-07 5.3424L-3.73488e-07 6.67541L3.78639 6.68376L9.74488 6.69694L9.3782 6.92213C8.42695 7.50629 7.67382 8.65219 7.23031 10.1903C7.10393 10.6285 6.91323 11.6274 6.94785 11.6698Z" fill="currentColor"/>',
    '</svg>'
];
const ARROW_LEFT_SVG = [
    '<svg class="icon-arrow-left" width="100%" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">',
    '<path d="M5.15567 0.3302C5.14651 0.319 4.86561 0.2337 4.53152 0.1406C4.19738 0.0474995 3.92038 -0.0144006 3.91593 0.00289941C3.91152 0.0201994 3.87694 0.301299 3.83914 0.627599C3.7632 1.2828 3.57933 2.04774 3.38286 2.52616C2.77046 4.01716 1.67172 5.00441 0.339017 5.26101L-0.000283612 5.32637L-0.000283669 5.98502L-0.000283727 6.64372L0.307216 6.70628C2.32241 7.11607 3.63618 8.94241 3.88195 11.6756C3.89799 11.854 3.91716 12 3.92454 12C3.99251 12 5.11863 11.671 5.13993 11.645C5.15533 11.6261 5.14388 11.4588 5.11443 11.2732C4.78857 9.21822 3.85552 7.546 2.64471 6.8468L2.35427 6.67907L8.32685 6.66594L12.1035 6.6576L12.1035 5.32459L8.31713 5.31624L2.35864 5.30306L2.72532 5.07787C3.67657 4.49371 4.4297 3.34781 4.87321 1.8097C4.99959 1.3715 5.19029 0.3726 5.15567 0.3302Z" fill="currentColor"/>',
    '</svg>'
];

function ensureNavigationElements(swiperElement) {
    let prevEl = swiperElement.querySelector('.swiper-prev');
    let nextEl = swiperElement.querySelector('.swiper-next');
    const wrapper = swiperElement.querySelector('.swiper-wrapper');

    if (!prevEl) {
        prevEl = document.createElement('button');
        prevEl.className = 'swiper-button-prev swiper-prev';
        // Insert before wrapper so arrows come first in tab order
        if (wrapper) {
            swiperElement.insertBefore(prevEl, wrapper);
        } else {
            swiperElement.prepend(prevEl);
        }
    }

    if (!nextEl) {
        nextEl = document.createElement('button');
        nextEl.className = 'swiper-button-next swiper-next';
        if (wrapper) {
            swiperElement.insertBefore(nextEl, wrapper);
        } else {
            swiperElement.prepend(nextEl);
        }
    }

    prevEl.ariaLabel = 'Previous slide';
    nextEl.ariaLabel = 'Next slide';

    return { prevEl, nextEl };
}

function setupSlideToControls(swiperElement, swiperInstance) {
    const controls = Array.from(
        swiperElement.querySelectorAll('[data-swiper-slide-to]')
    );

    if (controls.length === 0) return;

    const getCurrentIndex = () => {
        // For looped swipers, `realIndex` maps to the original slide.
        if (swiperInstance.params?.loop) return swiperInstance.realIndex;
        return swiperInstance.activeIndex;
    };

    const updateActive = () => {
        const currentIndex = getCurrentIndex();
        controls.forEach((control) => {
            const targetIndex = Number(control.dataset.swiperSlideTo);
            control.classList.toggle('is-active', targetIndex === currentIndex);
        });
    };

    const goTo = (targetIndex) => {
        if (Number.isNaN(targetIndex)) return;
        if (
            swiperInstance.params?.loop &&
            typeof swiperInstance.slideToLoop === 'function'
        ) {
            swiperInstance.slideToLoop(targetIndex);
            return;
        }

        swiperInstance.slideTo(targetIndex);
    };

    controls.forEach((control) => {
        const onActivate = (event) => {
            // Allow buttons/links to still be focusable but prevent navigation.
            event.preventDefault();
            const targetIndex = Number(control.dataset.swiperSlideTo);
            goTo(targetIndex);
        };

        control.addEventListener('click', onActivate);
        control.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') onActivate(event);
        });
    });

    // Keep state synced
    updateActive();
    swiperInstance.on('slideChange', updateActive);
    swiperInstance.on('realIndexChange', updateActive);
}

async function injectNavigationIcons({ prevEl, nextEl }) {
    const [leftSvg, rightSvg] = await Promise.all([
        ARROW_LEFT_SVG,
        ARROW_RIGHT_SVG
    ]);

    if (leftSvg) {
        prevEl.innerHTML = leftSvg;
        prevEl.classList.add('has-custom-icon');
    }

    if (rightSvg) {
        nextEl.innerHTML = rightSvg;
        nextEl.classList.add('has-custom-icon');
    }
}

function setupTwoPhotoContent(swiperElement, swiperInstance) {
    const section = swiperElement.closest('[data-two-photo-carousel-section]');
    const progressFill = section?.querySelector(
        '[data-two-photo-progress-fill]'
    );
    if (!section) return;

    const totalSlides = swiperInstance.slides.length;

    const updateContent = () => {
        const currentIndex = swiperInstance.params?.loop
            ? swiperInstance.realIndex
            : swiperInstance.activeIndex;
        const slidesPerView =
            Math.floor(swiperInstance.params.slidesPerView) || 1;

        // Update Alpine data
        if (window.Alpine && section._x_dataStack && section._x_dataStack[0]) {
            section._x_dataStack[0].currentIndex = currentIndex;
            // Hide nav when all slides are visible
            section._x_dataStack[0].showNav = totalSlides > slidesPerView;
        }

        // Update progress bar - account for slidesPerView
        if (progressFill && totalSlides > 0) {
            const maxIndex = Math.max(totalSlides - slidesPerView, 0);
            const progress =
                maxIndex > 0 ? (currentIndex / maxIndex) * 100 : 100;
            progressFill.style.width = `${progress}%`;
        }
    };

    // Initial update
    setTimeout(updateContent, 0);

    // Update on slide change
    swiperInstance.on('slideChange', updateContent);
    swiperInstance.on('realIndexChange', updateContent);
    swiperInstance.on('resize', updateContent);
}

function setupSuggestedDestinationsProgress(swiperElement, swiperInstance) {
    const section = swiperElement.closest(
        '[data-suggested-destinations-carousel-section]'
    );
    const progressFill = section?.querySelector(
        '[data-suggested-destinations-progress-fill]'
    );
    if (!section) return;

    const totalSlides = swiperInstance.slides.length;

    const updateContent = () => {
        const currentIndex = swiperInstance.params?.loop
            ? swiperInstance.realIndex
            : swiperInstance.activeIndex;
        const slidesPerView =
            Math.floor(swiperInstance.params.slidesPerView) || 1;

        // Update Alpine data
        if (window.Alpine && section._x_dataStack && section._x_dataStack[0]) {
            section._x_dataStack[0].currentIndex = currentIndex;
            // Hide nav when all slides are visible
            section._x_dataStack[0].showNav = totalSlides > slidesPerView;
        }

        // Update progress bar - account for slidesPerView
        if (progressFill && totalSlides > 0) {
            const maxIndex = Math.max(totalSlides - slidesPerView, 0);
            const progress =
                maxIndex > 0 ? (currentIndex / maxIndex) * 100 : 100;
            progressFill.style.width = `${progress}%`;
        }
    };

    // Initial update
    setTimeout(updateContent, 0);

    // Update on slide change
    swiperInstance.on('slideChange', updateContent);
    swiperInstance.on('realIndexChange', updateContent);
    swiperInstance.on('resize', updateContent);
}

/**
 * Set `inert` on non-visible slides so Tab skips them entirely.
 * Visible slides are determined from activeIndex + slidesPerView.
 * Nav arrows are siblings of .swiper-wrapper, so they stay focusable.
 */
function updateSlideInert(swiper) {
    const slides = swiper.slides;
    if (!slides?.length) return;

    const activeIdx = swiper.activeIndex;
    const perView = Math.max(1, Math.floor(swiper.params.slidesPerView) || 1);

    for (let i = 0; i < slides.length; i++) {
        const isVisible = i >= activeIdx && i < activeIdx + perView;
        slides[i].toggleAttribute('inert', !isVisible);
    }
}

function setupAutoplayToggle(swiperElement, swiperInstance) {
    const btn = swiperElement.querySelector('[data-swiper-autoplay-toggle]');
    if (!btn) return;

    const pauseIcon = btn.querySelector('.swiper-autoplay-toggle__pause');
    const playIcon = btn.querySelector('.swiper-autoplay-toggle__play');

    const update = (playing) => {
        pauseIcon.classList.toggle('hidden!', !playing);
        playIcon.classList.toggle('hidden!', playing);
        btn.setAttribute(
            'aria-label',
            playing ? 'Pause autoplay' : 'Play autoplay'
        );
    };

    btn.addEventListener('click', () => {
        if (swiperInstance.autoplay.running) {
            swiperInstance.autoplay.stop();
            update(false);
        } else {
            swiperInstance.autoplay.start();
            update(true);
        }
    });
}

function initializeSwiperElement(swiperElement, config) {
    const handle = swiperElement.dataset.swiperHandle ?? 'default';
    let finalConfig = config[handle] ?? config['default'];

    if (
        handle === 'mosaicCarousel' ||
        handle === 'listingCarousel' ||
        handle === 'reviewsCarousel' ||
        handle === 'featuresCarousel' ||
        handle === 'propertyThumbnailGallery'
    ) {
        const { prevEl, nextEl } = ensureNavigationElements(swiperElement);
        injectNavigationIcons({ prevEl, nextEl });

        finalConfig = {
            ...finalConfig,
            navigation: {
                ...(finalConfig.navigation ?? {}),
                prevEl,
                nextEl
            }
        };
    }

    if (handle === 'twoPhotoCarousel') {
        // Navigation buttons are outside the swiper, in the top section
        const section = swiperElement.closest('section');
        const prevEl = section?.querySelector(
            '[data-two-photo-nav].swiper-prev'
        );
        const nextEl = section?.querySelector(
            '[data-two-photo-nav].swiper-next'
        );

        if (prevEl && nextEl) {
            injectNavigationIcons({ prevEl, nextEl });

            finalConfig = {
                ...finalConfig,
                navigation: {
                    ...(finalConfig.navigation ?? {}),
                    prevEl,
                    nextEl
                }
            };
        }
    }

    if (handle === 'suggestedDestinations') {
        // Navigation buttons are outside the swiper, in the header section
        const section = swiperElement.closest('section');
        const prevEl = section?.querySelector(
            '[data-suggested-destinations-nav].swiper-prev'
        );
        const nextEl = section?.querySelector(
            '[data-suggested-destinations-nav].swiper-next'
        );

        if (prevEl && nextEl) {
            injectNavigationIcons({ prevEl, nextEl });

            finalConfig = {
                ...finalConfig,
                navigation: {
                    ...(finalConfig.navigation ?? {}),
                    prevEl,
                    nextEl
                }
            };
        }
    }

    const slideCount =
        swiperElement.querySelector('.swiper-wrapper')?.children.length ?? 0;
    if (finalConfig?.loop && slideCount < 2) {
        finalConfig = {
            ...finalConfig,
            loop: false
        };
    }

    const thisSwiper = new Swiper(swiperElement, finalConfig);

    // Make non-visible slides inert for keyboard accessibility.
    // This prevents Tab from reaching off-screen slides and also
    // fixes the browser focus-scroll displacement bug (since inert
    // elements can't receive focus, the browser never scrolls to them).
    updateSlideInert(thisSwiper);
    thisSwiper.on('slideChange', () => updateSlideInert(thisSwiper));
    thisSwiper.on('breakpoint', () => updateSlideInert(thisSwiper));

    setupSlideToControls(swiperElement, thisSwiper);

    // Autoplay pause/play toggle
    if (finalConfig.autoplay) {
        setupAutoplayToggle(swiperElement, thisSwiper);
    }

    // Handle dynamic content updates for two-photo carousel
    if (handle === 'twoPhotoCarousel') {
        setupTwoPhotoContent(swiperElement, thisSwiper);
    }

    // Handle progress bar for suggested destinations carousel
    if (handle === 'suggestedDestinations') {
        setupSuggestedDestinationsProgress(swiperElement, thisSwiper);
    }

    // Dispatch slidechange event for Alpine.js integration
    if (handle === 'featuresCarousel') {
        const dispatchSlideChange = () => {
            const activeIndex = thisSwiper.params?.loop
                ? thisSwiper.realIndex
                : thisSwiper.activeIndex;
            window.dispatchEvent(
                new CustomEvent('slidechange', {
                    detail: { handle, activeIndex }
                })
            );
        };
        thisSwiper.on('slideChange', dispatchSlideChange);
        thisSwiper.on('realIndexChange', dispatchSlideChange);
    }

    return thisSwiper;
}

const swiper = {
    config: {
        default: {
            modules: [Navigation, Pagination]
        },
        mosaicCarousel: {
            modules: [Navigation, Pagination, Autoplay],
            slidesPerView: 1,
            loop: true,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false
            },
            pagination: {
                el: '.swiper-pagination',
                clickable: true
            }
        },
        propertyThumbnailGallery: {
            modules: [Navigation, Pagination],
            slidesPerView: 1,
            loop: false,
            pagination: {
                el: '.swiper-pagination',
                clickable: true
            }
        },
        destinationsCarousel: {
            modules: [EffectFade],
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            slidesPerView: 1
        },
        listingCarousel: {
            modules: [Navigation],
            slidesPerView: 2.2,
            slidesPerGroup: 2,
            loop: false,
            pagination: {
                el: '.swiper-pagination',
                clickable: true
            },
            breakpoints: {
                640: {
                    slidesPerView: 3.2,
                    slidesPerGroup: 3
                },
                1024: {
                    slidesPerView: 5,
                    slidesPerGroup: 5
                },
                1280: {
                    slidesPerView: 5,
                    slidesPerGroup: 5
                }
            }
        },
        reviewsCarousel: {
            modules: [Navigation],
            slidesPerView: 1.08,
            slidesPerGroup: 1,
            spaceBetween: 12,
            loop: false,
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    slidesPerGroup: 2
                },
                1024: {
                    slidesPerView: 3,
                    slidesPerGroup: 3,
                    spaceBetween: 14
                }
            }
        },
        twoPhotoCarousel: {
            modules: [Navigation],
            slidesPerView: 1,
            loop: true
        },
        featuresCarousel: {
            modules: [Navigation, EffectFade],
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            slidesPerView: 1,
            loop: true
        },
        suggestedDestinations: {
            modules: [Navigation],
            slidesPerView: 1.2,
            slidesPerGroup: 1,
            spaceBetween: 12,
            loop: false,
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    slidesPerGroup: 2
                },
                1024: {
                    slidesPerView: 3,
                    slidesPerGroup: 3
                }
            }
        }
    },
    activeSwipers: [],
    init() {
        const swiperElements = document.querySelectorAll('.swiper');
        if (swiperElements.length === 0) return;

        swiperElements.forEach((swiperElement) => {
            // Skip if already initialized
            if (swiperElement.swiper) return;

            const thisSwiper = initializeSwiperElement(
                swiperElement,
                this.config
            );
            this.activeSwipers.push(thisSwiper);
        });
    },
    refresh(container = document) {
        // Initialize any new swiper elements within the container
        const swiperElements = container.querySelectorAll('.swiper');
        if (swiperElements.length === 0) return;

        swiperElements.forEach((swiperElement) => {
            // Skip if already initialized
            if (swiperElement.swiper) return;

            const thisSwiper = initializeSwiperElement(
                swiperElement,
                this.config
            );
            this.activeSwipers.push(thisSwiper);
        });
    }
};

export default swiper;
